<template>
  <sub-section :title="title" helpTitle="Your defined contribution pension pots" :showHelp="showHelp" :helpText="htmlArray('haves.pension-pots.help')">
    <slot name="message"></slot>
    <div v-show="hasPensionPots" class="table-responsive mb-2">
      <table class="table table-striped align-middle">
        <thead>
          <tr>
            <th v-if="selectionEnabled">{{ selectedColumnTitle }}</th>
            <th>Provider</th>
            <th v-if="showValuation" class="text-nowrap">Valuation</th>
            <th class="numeric contribution-column" :class="{ compact: showValuation && estRetirementValueColumnVisible }">
              Personal contrib.
              <help v-if="showCurrentContributionHelp" title="Pension contributions"
                :text="textArray('haves.pension-pots.contributions.help')" />
            </th>
            <th class="numeric contribution-column" :class="{ compact: showValuation && estRetirementValueColumnVisible }">
              Employer contrib.
            </th>
            <th class="numeric">
              Est. current value
              <help title="Estimated current value" :text="currentValueHelpText" />
            </th>
            <th v-if="estRetirementValueColumnVisible" class="numeric">
              Est. at retirement value
              <help title="Estimated value at retirement" :text="HelpText.PensionPotsEstValAtRetirement()" />
            </th>
            <th v-if="showEditButtons"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pensionPot in getPensionPots" v-bind:key="pensionPot.name">
            <td v-if="selectionEnabled">
              <div class="form-check form-switch">
                <input
                  type="checkbox"
                  class="form-check-input todays-money-chk"
                  role="switch"
                  :id="pensionPot.id.toString()"
                  :value="pensionPot.id"
                  v-model="selectedPensionPotIds"
                  :disabled="!selectablePensionPotIds.includes(pensionPot.id) || isOnlySelectedPot(pensionPot.id)"
                />
              </div>
            </td>
            <td :class="{'text-nowrap': pensionPot.name.startsWith('Pension Pot')}">{{ pensionPot.name }}</td>
            <td v-if="showValuation" class="text-nowrap">
              {{ formatter.formatCurrency(pensionPot.valuationAmount) }}
              <span class="fst-italic">({{ getValuationDateDescription(pensionPot.valuationDate) }})</span>
            </td>
            <td class="numeric contribution-column" :class="{ compact: showValuation && estRetirementValueColumnVisible }">
              {{ formatter.formatCurrency(pensionPot.personalMonthlyContribution) }}
            </td>
            <td class="numeric contribution-column" :class="{ compact: showValuation && estRetirementValueColumnVisible }">
              {{ formatter.formatCurrency(pensionPot.employerMonthlyContribution) }}
            </td>
            <td class="numeric">
              {{ formatter.formatCurrency(pensionPot.estimatedValueAtPresent) }}
            </td>
            <td v-if="estRetirementValueColumnVisible" class="numeric">
              {{ formatter.formatCurrency(pensionPot.estimatedValueAtRetirement) }}
            </td>
            <td v-if="showEditButtons">
              <three-dot-menu
                :id="pensionPot.id"
                :permissions="getPensionPermissions(pensionPot)"
                :aria-purpose="pensionPot.name"
                @viewClicked="onView"
                @editClicked="onEdit"
                @deleteClicked="onDelete" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-show="!hasPensionPots">
        <empty-asset name="defined contribution pension pots" />
    </div>

    <div v-if="showAddButton" class="row">
      <add-asset ref="addAssetComponent" name="pot" longName="another defined contribution pension pot manually" instruction="Click Add (+) to add more manually." @addClicked="onAdd()" />
    </div>

    <div v-if="showAdvice && showLumpSumInfo" class="row mt-3">
      <div class="col d-flex align-items-center">
        <p class="text-muted align-middle mb-0">
          {{ lumpSumInfo }}
        </p>
      </div>
    </div>

    <div v-if="showAdvice && showGuaranteedIncomeInfo" class="mt-3">
        <div class="col border-top pt-3">
            <p class="text-muted">
                {{ guaranteedIncomeText }}
                <help title="Guaranteed income" :text="HelpText.guaranteedIncome(annuityType)" />
            </p>
            <div class="d-grid gap-2 d-sm-block">
                <button type="button" class="btn btn-dark me-sm-2" @click="onUnsubscribeGuaranteedIncomeClicked($event)">Remove</button>
                <button type="button" class="btn btn-primary" @click="emit('guaranteed-income-change-required')">Change</button>
            </div>
        </div>
    </div>

    <template v-if="showAdvice && canLinkLiveValues">
      <div v-if="!isLinkedToLiveValues" class="alert alert-info mb-2 mt-3">
        <i class="bi bi-database-add me-2"></i>
        Add live pots via our
        <a @click="onTransferToPensionLab($event)" href="" class="alert-link skin-link me-1">live dashboard setup</a>
        <help title="Live pension pots" :text="HelpText.livePensionPots()" />
      </div>
      <div v-else class="alert alert-secondary mb-2 mt-3">
        <i class="bi bi-info-circle-fill"></i>
        {{ linkMoreLiveFundsAdviceText }}
        <a @click="onTransferToPensionLab($event)" href="" class="alert-link">here</a>
      </div>
    </template>

    <div v-if="showPresentTotalValue">
      <p>
        The total estimated current value of your selected pension pots is <strong>{{ formatter.formatCurrency(estimatedTotalPresentValue) }}</strong>.
      </p>
    </div>

    <slot name="lower-area"></slot>
  </sub-section>

  <modal :title="modalTitle" ref="potModal" idKey="pensionPot" close-button-aria-label="Close defined contribution pension pot popup editor" :returnFocusTo="addAssetButton">
    <pension-pot-editor ref="potEditor" @edit-finished="onEditFinished" :pension-pot="pensionPotToEdit" />
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, PropType } from "vue";
import { PensionPot } from "@/guiide/ts/pensionPot";
import Modal from "@/guiide/components/common/Modal.vue";
import PensionPotEditor from "@/guiide/components/assets/PensionPotEditor.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import AddAsset from "@/guiide/components/assets/AddAsset.vue";
import EmptyAsset from "@/guiide/components/assets/EmptyAsset.vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import PermissionService from "@/guiide/ts/permissionService";
import Constants from "@/guiide/ts/constants";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import ThreeDotMenu from "@/guiide/components/common/ThreeDotMenu.vue";
import { useStore } from "vuex";
import { formatter } from "../../../shared/ts/formatter";
import { app } from "@/guiide/ts/app";
import Monitoring from "@/guiide/ts/monitoring/monitoring";
import { GuaranteedIncome } from "@/guiide/ts/guaranteedIncome";
import View from "@/guiide/ts/view";
import AnnuityType from "@/guiide/ts/annuityType";
import { useUIContent } from "@/guiide/ts/configuration/useUIContent";
import { getValuationDateDescription } from "@/guiide/ts/utilities";

const props = defineProps({
    showValuation: { type: Boolean, default: true },
    showEstRetirementValue: { type: Boolean, required: false, default: true },
    showEditButtons: { type: Boolean, required: false, default: true },
    showAddButton: { type: Boolean, required: false, default: true },
    showAdvice: { type: Boolean, required: false, default: true },
    showHelp: { type: Boolean, required: false, default: true },
    title: { type: String, required: false, default: "Your defined contribution pension pots"},
    selectionEnabled: { type: Boolean, required: false, default: false },
    selectedColumnTitle: { type: String, required: false, default: "Included" },
    showPathlinesAlert: { type: Boolean, required: false, default: false },
    showPresentTotalValue: { type: Boolean, required: false, default: false },
    selectablePensionPotIds: { type: Array as PropType<Array<number>>, default: () => []},
    currentValueHelpText: { type: Array as PropType<Array<string>>, default: () => HelpText.PensionPotsCurrentValue()}
});

const selectedPensionPotIds = defineModel<number[]>(
    'selectedPensionPotIds',
    { default: () => [] }
);

const emit = defineEmits(['guaranteed-income-change-required']);

const store = useStore();
const { textArray, htmlArray } = useUIContent();

const potModal = ref(null);
const modalTitle = ref("");
const pensionPotToEdit = ref(ObjectBuilder.createPensionPot(Constants.NewId, "", 0, 0, 0, 0, false, 0, 0, true, true, true));
const selectedPensionPots = ref([]);

const addAssetButton = ref<HTMLElement>(null);
const addAssetComponent = ref<InstanceType<typeof AddAsset> | null>(null);

onMounted(() => {
  selectedPensionPots.value = getPensionPots.value;
});

const hasPensionPots = computed<boolean>(() => {
  return store.state.retiree?.pensionPots.pots.length > 0;
});

const getPensionPots = computed<Array<PensionPot>>(() => {
  return (
    store.state.retiree?.pensionPots.pots ?? new Array<PensionPot>()
  );
});

const canLinkLiveValues = computed<boolean>(() => {
  if (store.state.view == View.Haves) {
    return store.state.configuration.canLinkLiveValuesFromHaves;
  } else if (
    store.state.view == View.Dashboard ||
    store.state.view == View.GuiidePlus
  ) {
    return store.state.configuration.canLinkLiveValuesFromDashboard;
  } else {
    return false;
  }
});

const isLinkedToLiveValues = computed<boolean>(() => {
  return store.state.retiree?.isLinkedToLiveValues ?? false;
});

const linkMoreLiveFundsAdviceText = computed<string>(() => {
  return store.state.configuration.liveValuesLinkMoreFundsAdviceText;
});

const showCurrentContributionHelp = computed<boolean>(() => {
  return store.state.configuration
    .dashboardShowCurrentContributionHelp;
});

const showLumpSumInfo = computed<boolean>(() => {
  if (store.state.retiree == null) {
    return false;
  }
  if (store.state.retiree.alreadyDrawingPension) {
    return false;
  }
  return (
    store.state.retiree.requirements.takingTaxFreeAsLumpSum && hasPensionPots.value
  );
});

const lumpSumInfo = computed<string>(() => {
  if (!showLumpSumInfo.value) {
    return "";
  }

  const lumpSumAmount = store.state.retiree.pensionPots.lumpSumValueAtRetirement;
  let lumpSumAmountFormatted: string;
  let reductionStatement: string;

  if (lumpSumAmount < Constants.TaxFreeCashLimit) {
    lumpSumAmountFormatted = formatter.formatCurrency(lumpSumAmount);
    reductionStatement = `The total estimated figure at retirement will immediately be reduced
      by ${lumpSumAmountFormatted} as you have chosen to take lump sum tax free cash.`;
  } else {
    lumpSumAmountFormatted = formatter.formatCurrency(Constants.TaxFreeCashLimit);
    reductionStatement = `The total estimated figure at retirement will immediately be reduced
      by ${lumpSumAmountFormatted} (the current maximum tax free cash possible) as you have 
      chosen to take lump sum tax free cash.`;
  }

  return `${reductionStatement} We assume you will spend this, but you can add this
      back into other cash below at your retirement date if not.`
});

const showGuaranteedIncomeInfo = computed<boolean>(() => {
  if (!store.state.configuration.guaranteedIncomeEnabled) {
    return false;
  }
  return store.state.retiree?.guaranteedIncome.subscribed ?? false;
});

const annuityType = computed<AnnuityType>(() => {
  return store.state.configuration.annuityType;
});

const guaranteedIncomeText = computed<string>(() => {
  if (!showGuaranteedIncomeInfo.value) {
    return "";
  }

  const guaranteedIncome = store.state.retiree.guaranteedIncome as GuaranteedIncome;
  const allocationAmount = formatter.formatCurrency(guaranteedIncome.allocationAmount);
  const annualIncome = formatter.formatCurrency(guaranteedIncome.annualIncome);
  const subscriptionDateFormatted = formatter.monthAndYearDate(store.state.retiree.guaranteedIncome.subscriptionDate);

  let remaining = "";
  if (store.state.retiree.requirements.takingTaxFreeAsLumpSum) {
    remaining = "remaining ";
  }

  const pot = getPensionPots.value.length == 1 ? "pot" : "pots";

  switch (annuityType.value) {
    case AnnuityType.GuaranteedIncome:
      return (
        `You have chosen to see how ${guaranteedIncome.allocation}% (${allocationAmount}) of your ${remaining}non protected pension ${pot} could be used to buy a guaranteed` +
        ` income paid into your ${pot} from ${subscriptionDateFormatted}. On the illustrative example, it would mean a guaranteed income of ${annualIncome}.`
      );
    case AnnuityType.Traditional:
      return `You have chosen to use ${guaranteedIncome.allocation}% (${allocationAmount}) of your ${remaining} non protected pension ${pot} to model an estimated guaranteed` +
        ` income of ${annualIncome}, which has been added to other income below.`

    default:
      throw new Error("Unknown annuity type.");
  }
});

const estimatedTotalPresentValue = computed<number>(() => {
  if (store.state.retiree == null) {
    return 0;
  } else {
    return getPensionPots.value
      .filter(pp => selectedPensionPotIds.value.includes(pp.id))
      .reduce((total, pp) => total + pp.estimatedValueAtPresent, 0);
  }
});

const estRetirementValueColumnVisible = computed<boolean>(() => {
  if (!props.showEstRetirementValue) {
    return false;
  }

  if (!store.state.retiree) {
    return props.showEstRetirementValue;
  }

  return !store.state.retiree.alreadyDrawingPension;
});

function getPensionPermissions(pensionPot: PensionPot) {
  return PermissionService.getPermissionSet(pensionPot);
}

function onEditFinished() {
  potModal.value.closeModal();
}

function onAdd() {
  modalTitle.value = "Add defined contribution pension pot";
  const pensionPot = ObjectBuilder.createPensionPot(Constants.NewId, "", 0, 0, 0, 0, false, 0, 0, true, true, true);
  pensionPotToEdit.value = pensionPot;

  addAssetButton.value = addAssetComponent.value.getAddButton();

  potModal.value.showModal();
}

function onEdit(id: number) {
  const pensionPot = getPensionPots.value.filter((p) => p.id == id)[0];
  showPensionEditor("Edit defined contribution pension pot", pensionPot);
}

function onView(id: number) {
  const pensionPot = getPensionPots.value.filter((p) => p.id == id)[0];
  showPensionEditor("View defined contribution pension pot", pensionPot);
}

function showPensionEditor(title: string, pensionPot: PensionPot) {
  modalTitle.value = title;
  pensionPotToEdit.value = pensionPot;
  potModal.value.showModal();
}

async function onDelete(id: number) {
  await app.deletePensionPot(id);
}

async function onTransferToPensionLab(event: any) {
  event.preventDefault();
  Monitoring.pensionLabDashboardEvent(store.state.view);
  await app.transferToPensionLab();
}

async function onUnsubscribeGuaranteedIncomeClicked(event: any) {
  event.preventDefault();
  await app.deleteGuaranteedIncome();
}

function isOnlySelectedPot(pensionPotId: number): boolean {
    return selectedPensionPotIds.value.length === 1 && selectedPensionPotIds.value[0] === pensionPotId;
}

</script>

<style scoped>
th.numeric {
  text-align: right;
}

td.numeric {
  text-align: right;
}

th.contribution-column {
    white-space: normal;
}

th.contribution-column.compact {
    width: 1%;
}

td.contribution-column {
    white-space: nowrap;
}

td.contribution-column.compact {
    width: 1%;
}

.card-body .btn-dark {
    padding: 12px 34px !important;
}

@media (min-width: 768px) {
  .table-responsive {
    overflow: visible;
  }
}
</style>
